
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tradingSalesOrderService from "../services/tradingSalesOrderService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: { margin: "7px 0 0", color: "#747b8d", fontSize: "13px" },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "18px",
  },
  sectionTitle: { margin: "0 0 18px", fontSize: "16px", fontWeight: 700 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "20px",
  },
  label: {
    color: "#747b8d",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: "7px",
    letterSpacing: ".3px",
  },
  value: { fontSize: "14px", fontWeight: 600, overflowWrap: "anywhere" },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 14px",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  badge: {
    display: "inline-block",
    borderRadius: "20px",
    padding: "6px 10px",
    fontSize: "11px",
    fontWeight: 700,
    background: "#efedff",
    color: "#5548cb",
    whiteSpace: "nowrap",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "900px" },
  th: {
    textAlign: "left",
    padding: "12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "13px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    verticalAlign: "top",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: { color: "#858b9b", fontSize: "13px" },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "24px",
    padding: "9px 0",
    fontSize: "13px",
  },
};

const formatStatus = (value) =>
  String(value || "DRAFT").replace(/_/g, " ");

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  return parts.length === 3
    ? `${parts[2]}-${parts[1]}-${parts[0]}`
    : raw;
};

const formatAmount = (value, currency = "INR") => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "—";

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
};

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

function Field({ label, value }) {
  return (
    <div>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>
        {value === null || value === undefined || value === ""
          ? "—"
          : value}
      </div>
    </div>
  );
}

function TotalRow({ label, value, currency, strong = false }) {
  return (
    <div
      style={{
        ...styles.totalRow,
        fontWeight: strong ? 750 : 500,
        borderTop: strong ? "1px solid #e8eaf2" : 0,
        marginTop: strong ? "8px" : 0,
        paddingTop: strong ? "15px" : "9px",
      }}
    >
      <span>{label}</span>
      <span>{formatAmount(value, currency)}</span>
    </div>
  );
}

export default function TradingSalesOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [history, setHistory] = useState([]);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await tradingSalesOrderService.getSalesOrder(id);
      const body = getResponseData(response);

      if (!body || !body.id) {
        throw new Error("Sales Order details were not returned by the API.");
      }

      setOrder(body);

      try {
        const historyResponse =
          await tradingSalesOrderService.getAuditHistory(id);
        const historyBody = getResponseData(historyResponse);
        setHistory(Array.isArray(historyBody) ? historyBody : []);
      } catch {
        // The main detail screen remains usable if history cannot be loaded.
        setHistory([]);
      }
    } catch (err) {
      setOrder(null);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load Sales Order details."
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const submitForApproval = async () => {
    if (!order) return;

    if (
      !window.confirm(
        `Submit Sales Order ${order.sales_order_no} for approval?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    setError("");

    try {
      await tradingSalesOrderService.submitForApproval(id, {
        remarks: remarks.trim() || undefined,
      });
      setRemarks("");
      await loadOrder();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to submit Sales Order for approval."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const cancelOrder = async () => {
    if (!cancelReason.trim()) {
      setError("Please enter a cancellation reason.");
      return;
    }

    if (
      !window.confirm(
        `Cancel Sales Order ${order.sales_order_no}?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    setError("");

    try {
      await tradingSalesOrderService.cancelSalesOrder(
        id,
        cancelReason.trim()
      );
      setCancelReason("");
      setShowCancel(false);
      await loadOrder();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to cancel Sales Order."
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !order) {
    return (
      <div style={styles.page}>
        <p style={styles.muted}>Loading Sales Order details...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>Sales Order Details</h1>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/sales-orders")}
          >
            Back to Sales Orders
          </button>
        </div>
        {error && <div role="alert" style={styles.error}>{error}</div>}
        <button
          type="button"
          style={styles.button}
          onClick={loadOrder}
        >
          Retry
        </button>
      </div>
    );
  }

  const currency = order.currency_code || "INR";
  const status = String(order.status_code || "DRAFT").toUpperCase();
  const canSubmit = status === "DRAFT";
  const canCancel = ["DRAFT", "PENDING_APPROVAL"].includes(status);
  const items = Array.isArray(order.items) ? order.items : [];
  const charges = Array.isArray(order.charges) ? order.charges : [];
  const terms = Array.isArray(order.terms) ? order.terms : [];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            {order.sales_order_no || `Sales Order ${order.id}`}
          </h1>
          <p style={styles.subtitle}>
            Trading Sales Order details and commercial summary
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/sales-orders")}
          >
            Back to List
          </button>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={loadOrder}
            disabled={loading || actionLoading}
          >
            Refresh
          </button>
          {canCancel && (
            <button
              type="button"
              style={{
                ...styles.secondaryButton,
                color: "#b42318",
              }}
              onClick={() => setShowCancel((value) => !value)}
              disabled={actionLoading}
            >
              Cancel Sales Order
            </button>
          )}
          {canSubmit && (
            <button
              type="button"
              style={styles.button}
              onClick={submitForApproval}
              disabled={actionLoading}
            >
              {actionLoading ? "Processing..." : "Submit For Approval"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
        </div>
      )}

      {showCancel && (
        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>Cancel Sales Order</h2>
          <label style={styles.label} htmlFor="cancelReason">
            Cancellation reason
          </label>
          <textarea
            id="cancelReason"
            rows={3}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder="Enter the reason for cancellation"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "12px",
              border: "1px solid #dfe2eb",
              borderRadius: "8px",
              font: "inherit",
              marginBottom: "12px",
            }}
          />
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => setShowCancel(false)}
              disabled={actionLoading}
            >
              Keep Order
            </button>
            <button
              type="button"
              style={{ ...styles.button, background: "#b42318" }}
              onClick={cancelOrder}
              disabled={actionLoading}
            >
              {actionLoading ? "Cancelling..." : "Confirm Cancellation"}
            </button>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Order Overview</h2>
        <div style={styles.grid}>
          <Field label="Sales Order No." value={order.sales_order_no} />
          <Field label="Order Date" value={formatDate(order.sales_order_date)} />
          <Field
            label="Requested Delivery"
            value={formatDate(order.requested_delivery_date)}
          />
          <div>
            <div style={styles.label}>Status</div>
            <span style={styles.badge}>{formatStatus(status)}</span>
          </div>
          <Field label="Quotation No." value={order.quotation_no} />
          <Field label="Quotation Version" value={order.quotation_version_no} />
          {/*<Field label="Customer ID" value={order.business_partner_id} />*/}
          <Field label="Customer Code" value={order.customer_code} />
          <Field label="Customer Name" value={order.customer_name} />
          <Field label="Customer Reference" value={order.customer_reference} />
          <Field label="Currency" value={currency} />
          <Field label="Exchange Rate" value={order.exchange_rate} />
          <Field label="Subject" value={order.subject} />
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Delivery Information</h2>
        <div style={styles.grid}>
          <Field
            label="Delivery Address"
            value={order.delivery_address}
          />
          <Field
            label="Shipping Instructions"
            value={order.shipping_instructions}
          />
          <Field label="Remarks" value={order.remarks} />
        </div>
      </div>

      
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Terms &amp; Conditions</h2>

        <p style={styles.muted}>
          Terms &amp; Conditions from the linked quotation
          {order.quotation_no ? ` ${order.quotation_no}` : ""}
        </p>

        {terms.length === 0 ? (
          <p style={styles.muted}>
            No Terms &amp; Conditions found on the linked quotation.
          </p>
        ) : (
          <div style={{ display: "grid", gap: "10px" }}>
            {terms.map((term, index) => (
              <div
                key={term.id ?? term.term_master_id ?? index}
                style={{
                  border: "1px solid #ecebfa",
                  background: "#fafaff",
                  borderRadius: "9px",
                  padding: "14px",
                }}
              >
                <div
                  style={{
                    fontWeight: 750,
                    fontSize: "12px",
                    marginBottom: "7px",
                  }}
                >
                  {term.term_name ??
                    term.name ??
                    term.title ??
                    `Term ${index + 1}`}
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    lineHeight: 1.7,
                    color: "#737991",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {term.value ??
                    term.term_value ??
                    term.description ??
                    "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      
      
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Order Items ({items.length})</h2>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Quotation Qty.</th>
                <th style={styles.th}>Ordered Qty.</th>
                <th style={styles.th}>Dispatched</th>
                <th style={styles.th}>Unit Price</th>
                <th style={styles.th}>Gross Amount</th>
                <th style={styles.th}>Discount</th>
                <th style={styles.th}>Taxable Amount</th>
                <th style={styles.th}>Tax</th>
                <th style={styles.th}>Line Total</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ ...styles.td, textAlign: "center" }}>
                    No order items found.
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={item.id || index}>
                    <td style={styles.td}>{item.item_seq_no || index + 1}</td>
                    <td style={{ ...styles.td, minWidth: "200px" }}>
                      {item.item_description || "—"}
                    </td>
                    <td style={styles.td}>{item.quotation_quantity ?? "—"}</td>
                    <td style={styles.td}>{item.ordered_quantity ?? "—"}</td>
                    <td style={styles.td}>{item.dispatched_quantity ?? 0}</td>
                    <td style={styles.td}>
                      {formatAmount(item.unit_selling_price, currency)}
                    </td>
                    <td style={styles.td}>
                      {formatAmount(item.line_gross_amount, currency)}
                    </td>
                    <td style={styles.td}>
                      {formatAmount(item.discount_amount, currency)}
                    </td>
                    <td style={styles.td}>
                      {formatAmount(item.taxable_amount, currency)}
                    </td>
                    <td style={styles.td}>
                      {formatAmount(item.tax_amount, currency)}
                    </td>
                    <td style={styles.td}>
                      <strong>
                        {formatAmount(item.line_total_amount, currency)}
                      </strong>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Additional Charges ({charges.length})</h2>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Charge Type</th>
                <th style={styles.th}>Payer</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>GST Rate</th>
                <th style={styles.th}>GST Amount</th>
                <th style={styles.th}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {charges.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...styles.td, textAlign: "center" }}>
                    No additional charges.
                  </td>
                </tr>
              ) : (
                charges.map((charge, index) => (
                  <tr key={charge.id || index}>
                    <td style={styles.td}>{charge.charge_type || "—"}</td>
                    <td style={styles.td}>{charge.payer_type || "—"}</td>
                    <td style={styles.td}>
                      {formatAmount(charge.charge_amount, currency)}
                    </td>
                    <td style={styles.td}>
                      {charge.gst_rate ?? 0}%
                    </td>
                    <td style={styles.td}>
                      {formatAmount(charge.gst_amount, currency)}
                    </td>
                    <td style={styles.td}>{charge.remarks || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Commercial Summary</h2>
        <div style={{ maxWidth: "440px", marginLeft: "auto" }}>
          <TotalRow
            label="Item Gross Amount"
            value={order.item_gross_amount}
            currency={currency}
          />
          <TotalRow
            label="Item Discount"
            value={order.item_discount_amount}
            currency={currency}
          />
          <TotalRow
            label="Item Taxable Amount"
            value={order.item_taxable_amount}
            currency={currency}
          />
          <TotalRow
            label="Item Tax"
            value={order.item_tax_amount}
            currency={currency}
          />
          <TotalRow
            label="Additional Charges"
            value={order.charge_amount}
            currency={currency}
          />
          <TotalRow
            label="Charge Tax"
            value={order.charge_tax_amount}
            currency={currency}
          />
          <TotalRow
            label="Total Tax"
            value={order.total_tax_amount}
            currency={currency}
          />
          <TotalRow
            label="Net Amount"
            value={order.net_amount}
            currency={currency}
            strong
          />
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Approval Information</h2>
        <div style={styles.grid}>
          <Field
            label="Submitted At"
            value={formatDate(order.submitted_for_approval_at)}
          />
          <Field label="Approved By (User ID)" value={order.approved_by} />
          <Field label="Approved At" value={formatDate(order.approved_at)} />
          <Field label="Rejected By (User ID)" value={order.rejected_by} />
          <Field label="Rejected At" value={formatDate(order.rejected_at)} />
          <Field label="Rejection Reason" value={order.rejection_reason} />
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Order History</h2>
        {history.length === 0 ? (
          <p style={styles.muted}>No history entries available.</p>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Event</th>
                  <th style={styles.th}>Field</th>
                  <th style={styles.th}>Old Value</th>
                  <th style={styles.th}>New Value</th>
                  <th style={styles.th}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry, index) => (
                  <tr key={entry.id || index}>
                    <td style={styles.td}>
                      {formatDate(entry.performed_at)}
                    </td>
                    <td style={styles.td}>{entry.event_type || "—"}</td>
                    <td style={styles.td}>{entry.field_name || "—"}</td>
                    <td style={styles.td}>{entry.old_value || "—"}</td>
                    <td style={styles.td}>{entry.new_value || "—"}</td>
                    <td style={styles.td}>{entry.remarks || entry.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canSubmit && (
        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>Submit for Approval</h2>
          <label style={styles.label} htmlFor="approvalRemarks">
            Remarks (optional)
          </label>
          <textarea
            id="approvalRemarks"
            rows={3}
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            placeholder="Add remarks for the approver"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "12px",
              border: "1px solid #dfe2eb",
              borderRadius: "8px",
              font: "inherit",
              marginBottom: "12px",
            }}
          />
          <button
            type="button"
            style={styles.button}
            onClick={submitForApproval}
            disabled={actionLoading}
          >
            {actionLoading ? "Submitting..." : "Submit For Approval"}
          </button>
        </div>
      )}
    </div>
  );
}
